import { useEffect, useState } from "react";
import { fetchDashboardCompanyStats } from "../../api/company";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  MapIcon,
} from "@heroicons/react/24/outline";

const statCardStyles = [
  {
    accent: "border-slate-900 bg-slate-950 text-white",
    iconWrap: "bg-white/10 text-white",
    eyebrow: "text-slate-300",
    subtitle: "text-slate-400",
    number: "text-white",
    arrow: "text-slate-300",
  },
  {
    accent: "border-slate-200 bg-white text-slate-950",
    iconWrap: "bg-sky-100 text-sky-700",
    eyebrow: "text-slate-500",
    subtitle: "text-slate-500",
    number: "text-slate-950",
    arrow: "text-slate-500",
  },
  {
    accent: "border-slate-200 bg-white text-slate-950",
    iconWrap: "bg-emerald-100 text-emerald-700",
    eyebrow: "text-slate-500",
    subtitle: "text-slate-500",
    number: "text-slate-950",
    arrow: "text-slate-500",
  },
  {
    accent: "border-slate-200 bg-white text-slate-950",
    iconWrap: "bg-amber-100 text-amber-700",
    eyebrow: "text-slate-500",
    subtitle: "text-slate-500",
    number: "text-slate-950",
    arrow: "text-slate-500",
  },
];

export const DashboardStatsSection = () => {
  const testStats = [{ id: 1, name: "Total Reports", value: 100 }];
  const [stats, setStats] = useState(testStats);

  const loadStats = async () => {
    await fetchDashboardCompanyStats()
      .then((res) => {
        setStats(res?.data?.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    loadStats();
  }, []);

  function nameToIcon(name) {
    switch (name) {
      case "REPORTS_NOT_FINISHED":
      case "REPORTS_FINISHED":
      case "REPORTS_NOT_APPROVED":
      case "REPORTS_AGREEMENT_NOT_CONFIRMED":
        return ClipboardDocumentCheckIcon;
      case "CASES_OPEN":
        return ChatBubbleLeftRightIcon;
      case "DEVIATIONS_OPEN_TOTAL":
      case "DEVIATIONS_OPEN_ASSIGNED":
        return ExclamationTriangleIcon;
      case "PROJECTS_OPEN":
        return FolderIcon;
      case "TRIPS_DISTANCE_TOTAL_CURRENT_MONTH":
      case "TRIPS_DISTANCE_USER_TOTAL_CURRENT_MONTH":
        return MapIcon;
      default:
        return BriefcaseIcon;
    }
  }

  function translateNameToTitle(name) {
    switch (name) {
      case "REPORTS_NOT_FINISHED":
      case "REPORTS_FINISHED":
        return "Rapporter";
      case "REPORTS_AGREEMENT_NOT_CONFIRMED":
        return "Rapportavtaler";
      case "REPORTS_NOT_APPROVED":
        return "Rapporter";
      case "CASES_OPEN":
        return "Saker";
      case "DEVIATIONS_OPEN_TOTAL":
      case "DEVIATIONS_OPEN_ASSIGNED":
        return "Avvik";
      case "PROJECTS_OPEN":
        return "Prosjekter";
      case "TRIPS_DISTANCE_TOTAL_CURRENT_MONTH":
      case "TRIPS_DISTANCE_USER_TOTAL_CURRENT_MONTH":
        return "Kilometer";
      default:
        return name;
    }
  }

  function translateNameToSubtitle(name) {
    switch (name) {
      case "REPORTS_NOT_FINISHED":
        return "Ikke ferdigstilt";
      case "REPORTS_FINISHED":
        return "Ferdigstilt";
      case "REPORTS_AGREEMENT_NOT_CONFIRMED":
        return "Ikke bekreftet";
      case "REPORTS_NOT_APPROVED":
        return "Ikke godkjent";
      case "CASES_OPEN":
        return "Under behandling";
      case "DEVIATIONS_OPEN_TOTAL":
        return "Venter behandling";
      case "DEVIATIONS_OPEN_ASSIGNED":
        return "Tildelt deg";
      case "PROJECTS_OPEN":
        return "Under utf�relse";
      case "TRIPS_DISTANCE_TOTAL_CURRENT_MONTH":
      case "TRIPS_DISTANCE_USER_TOTAL_CURRENT_MONTH":
        return "Denne m�neden";
      default:
        return name;
    }
  }

  function nameToPath(name) {
    switch (name) {
      case "REPORTS_NOT_FINISHED":
        return "/dashboard/service/?tab=2";
      case "REPORTS_FINISHED":
        return "/dashboard/service/reports?filters=view:FINISHED&page=1&per_page=50&sort=asc&sortBy=created_at&view=all";
      case "REPORTS_AGREEMENT_NOT_CONFIRMED":
        return "/dashboard/service/?user=1&date=2024-12-07&page=1&per_page=20&sort=asc&sortBy=time&view=all&tab=1";
      case "REPORTS_NOT_APPROVED": {
        const filtersParam = encodeURIComponent(
          JSON.stringify([{ name: "view", value: JSON.stringify(["NOT_APPROVED"]) }])
        );
        return `/dashboard/service/reports?filters=${filtersParam}&page=1&per_page=50&sort=asc&sortBy=created_at&view=all`;
      }
      case "CASES_OPEN":
        return "/dashboard/cases";
      case "DEVIATIONS_OPEN_TOTAL":
      case "DEVIATIONS_OPEN_ASSIGNED":
        return "/dashboard/hse-ks/deviations";
      case "PROJECTS_OPEN":
        return "/dashboard/projects";
      case "TRIPS_DISTANCE_TOTAL_CURRENT_MONTH":
        return "/dashboard/trips";
      case "TRIPS_DISTANCE_USER_TOTAL_CURRENT_MONTH":
      default:
        return "/dashboard";
    }
  }

  if (stats?.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        {stats?.map((stat, index) => {
          const style = statCardStyles[index % statCardStyles.length];
          const Icon = nameToIcon(stat.name);

          return (
            <Link to={nameToPath(stat.name)} key={stat.name} className="group block">
              <article
                className={`h-full rounded-2xl border px-3 py-3 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${style.accent}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${style.iconWrap}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <ArrowRightIcon className={`mt-0.5 h-3.5 w-3.5 shrink-0 transition group-hover:translate-x-0.5 ${style.arrow}`} />
                </div>

                <div className="mt-3">
                  <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${style.eyebrow}`}>
                    {translateNameToTitle(stat.name)}
                  </div>
                  <div className={`mt-1 text-2xl font-semibold tracking-tight ${style.number}`}>
                    {stat.count}
                  </div>
                  <p className={`mt-0.5 line-clamp-2 text-[11px] leading-4 ${style.subtitle}`}>
                    {translateNameToSubtitle(stat.name)}
                  </p>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

